import React, { useEffect } from 'react';
import clsx from 'clsx';
import { useEditor } from '@craftjs/core';
import { TextField as InspectorTextField } from '@/components/inspector-fields/TextField/TextField';
import { useFormFieldHiddenSync } from '@/hooks/useFormFieldHiddenSync';
import { NumberField } from '@/components/inspector-fields/NumberField/NumberField';
import { InfoTooltip } from '@/components/tooltips/InfoTooltip/InfoTooltip';
import IconClose from '@/assets/icons/icon-close.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { Switch } from '@/components/inspector-fields/Switch/Switch';
import type { FormTextFieldProps as SharedFormTextFieldProps } from '@builder4/shared/components/Form';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { FORM_TEXT_FIELD_NODE_NAME, FIELD_LABEL_TEXT } from '@/utils/constants';
// Removed local Toggle in favor of shared Switch component

interface FormTextFieldInspectorProps {
  className?: string;
  /** Title shown in the card header */
  title?: string;
  onClose?: () => void;
  /** The specific field node ID to edit */
  fieldNodeId: string;
  getDefaultFieldLabel: () => string;
}

export const FormTextField: React.FC<FormTextFieldInspectorProps> = ({
  className = '',
  title = 'Text Field',
  onClose,
  fieldNodeId,
  getDefaultFieldLabel,
}) => {
  const { actions, fieldProps } = useEditor((_, query) => {
    const node = query.node(fieldNodeId).get();
    return {
      fieldProps: (node?.data.props || {}) as SharedFormTextFieldProps,
    };
  });

  const { setBPProp, getBPProp } = useBreakpointAwareProp(FORM_TEXT_FIELD_NODE_NAME);

  // Read props using breakpoint-aware getter
  const label = getBPProp(fieldProps, 'label');
  const showLabel = getBPProp(fieldProps, 'showLabel');
  const placeholder = getBPProp(fieldProps, 'placeholder');
  const required = getBPProp(fieldProps, 'required');
  const hidden = getBPProp(fieldProps, 'hidden');
  const helpText = getBPProp(fieldProps, 'helpText');
  const showHelpText = getBPProp(fieldProps, 'showHelpText');
  const enforceCharacterLimit = getBPProp(fieldProps, 'enforceCharacterLimit');
  const minCharacterLimit = getBPProp(fieldProps, 'minCharacterLimit');
  const maxCharacterLimit = getBPProp(fieldProps, 'maxCharacterLimit');
  const urlMapping = getBPProp(fieldProps, 'urlMapping');

  // Throttled — batches rapid keystrokes (text inputs)
  const setProp = (callback: (props: SharedFormTextFieldProps) => void) => {
    actions.history.throttle(500).setProp(fieldNodeId, callback);
  };

  // Direct — discrete actions (toggles, number fields, onBlur)
  const setPropDirect = (callback: (props: SharedFormTextFieldProps) => void) => {
    actions.setProp(fieldNodeId, callback);
  };

  // Sync hidden prop with layer visibility
  const { handleHiddenChange } = useFormFieldHiddenSync<SharedFormTextFieldProps>(
    fieldNodeId,
    hidden,
    setPropDirect,
  );

  useEffect(() => {
    // WHY: ignore() prevents displayName sync from creating history entries that
    // compete with the actual prop change, which would make undo appear broken.
    actions.history.ignore().setCustom(fieldNodeId, (custom) => {
      custom.displayName = label || FIELD_LABEL_TEXT;
    });
  }, [label, actions, fieldNodeId]);

  return (
    <div
      className={clsx(
        'bg-neutral-0 flex h-[480px] max-h-[480px] w-[248px] flex-col overflow-hidden rounded-lg shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-neutral-100',
        className,
      )}
      data-craft-history
    >
      {/* Card header */}
      <div className="bg-neutral-0 inline-flex h-10 min-h-10 w-full items-center justify-between rounded-t-lg border border-neutral-100 px-3">
        <div className="text-xs leading-5 font-semibold text-neutral-900">{title}</div>
        <IconSimpleButton aria-label="Close" icon={IconClose} onClick={onClose} />
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col border border-t-0 border-neutral-100">
        <div className="no-scrollbar h-[440px] flex-1 overflow-y-auto">
          <div className="flex w-full flex-col items-start justify-start gap-3 px-3 pt-3 pb-4">
            {/* Field Label */}
            <InspectorTextField
              className="w-full"
              classNameInputParent="max-w-none"
              classNameInput="max-w-[140px]"
              text="Field Label"
              inputValue={label}
              placeholderText="Text"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setProp((props: SharedFormTextFieldProps) => {
                  setBPProp(props, 'label', e.target.value);
                })
              }
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                if (!e.target.value) {
                  setProp((props: SharedFormTextFieldProps) => {
                    setBPProp(props, 'label', getDefaultFieldLabel());
                  });
                }
              }}
            />
            {/* Placeholder */}
            <InspectorTextField
              className="w-full"
              classNameInputParent="max-w-none"
              classNameInput="max-w-[140px]"
              text="Placeholder"
              inputValue={placeholder}
              placeholderText="Enter your text"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setProp((props: SharedFormTextFieldProps) => {
                  setBPProp(props, 'placeholder', e.target.value);
                })
              }
            />

            {/* Label toggle */}
            <Switch
              label="Show Label"
              checked={showLabel}
              onCheckedChange={(checked: boolean) =>
                setPropDirect((props: SharedFormTextFieldProps) => {
                  setBPProp(props, 'showLabel', checked);
                })
              }
            />

            {/* Divider */}
            <div className="self-stretch pb-1">
              <div className="h-0 border-t border-neutral-100" />
            </div>

            {/* URL Mapping */}
            <div className="flex w-full flex-col gap-2">
              <div className="inline-flex items-center gap-1">
                <div className="text-button-sm leading-4 font-medium text-neutral-500">
                  URL Mapping
                </div>
                <InfoTooltip text="Pre-fill this field with data from your page URL. Enter the parameter name from your URL (like location or utm_source). For example, if you run ads targeting different cities with ?location=Chicago in the URL, enter location here and this field will automatically show 'Chicago' for visitors from that ad." />
              </div>
              <InspectorTextField
                className="w-full"
                classNameInputParent="w-full  max-w-full"
                classNameInput="max-w-full"
                text=""
                inputValue={urlMapping || ''}
                placeholderText="(Optional)"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setProp((props: SharedFormTextFieldProps) => {
                    setBPProp(props, 'urlMapping', e.target.value);
                  })
                }
              />
            </div>

            {/* Required / Hidden toggles */}
            <div className="flex w-full flex-col">
              <Switch
                label="This field is required"
                checked={required}
                onCheckedChange={(checked: boolean) =>
                  setPropDirect((props: SharedFormTextFieldProps) => {
                    setBPProp(props, 'required', checked);
                  })
                }
              />
              <Switch
                label="This is a hidden field"
                checked={hidden}
                onCheckedChange={handleHiddenChange}
              />

              {/* Include help text */}
              <Switch
                label="Include help text"
                checked={showHelpText}
                onCheckedChange={(checked: boolean) =>
                  setPropDirect((props: SharedFormTextFieldProps) => {
                    setBPProp(props, 'showHelpText', checked);
                  })
                }
              >
                <InspectorTextField
                  className="w-full"
                  classNameInputParent="max-w-none"
                  classNameInput="max-w-[160px]"
                  text="Text"
                  inputValue={helpText}
                  placeholderText="We only accept USDe.."
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setProp((props: SharedFormTextFieldProps) => {
                      setBPProp(props, 'helpText', e.target.value);
                    })
                  }
                />
              </Switch>

              {/* Character limit */}
              <Switch
                label="Set a character limit"
                checked={enforceCharacterLimit}
                onCheckedChange={(checked: boolean) =>
                  setPropDirect((props: SharedFormTextFieldProps) => {
                    setBPProp(props, 'enforceCharacterLimit', checked);
                  })
                }
              >
                <div className="flex w-full flex-col gap-2">
                  <div className="inline-flex w-full items-center justify-between">
                    <div className="text-button-sm leading-4 font-medium text-neutral-500">
                      Minimum Character Limit
                    </div>
                    <NumberField
                      className="w-auto"
                      classNameInput="w-12"
                      value={minCharacterLimit || 0}
                      min={0}
                      max={maxCharacterLimit || 500}
                      step={1}
                      arrows={true}
                      onChange={(val: number) => {
                        setPropDirect((props: SharedFormTextFieldProps) => {
                          setBPProp(props, 'minCharacterLimit', val === 0 ? null : val);
                        });
                      }}
                    />
                  </div>
                  <div className="inline-flex w-full items-center justify-between">
                    <div className="text-button-sm leading-4 font-medium text-neutral-500">
                      Maximum Character Limit
                    </div>
                    <NumberField
                      className="w-auto"
                      classNameInput="w-12"
                      value={maxCharacterLimit || 100}
                      min={minCharacterLimit || 0}
                      max={500}
                      step={1}
                      arrows={true}
                      onChange={(val: number) => {
                        setPropDirect((props: SharedFormTextFieldProps) => {
                          setBPProp(props, 'maxCharacterLimit', val === 0 ? null : val);
                        });
                      }}
                    />
                  </div>
                </div>
              </Switch>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormTextField;
